import AddButtonRounded from '@Components/AddButtonRounded';
import Flex from '@Components/Bases/Flex';
import { IntroductionButton, RemarkButton } from '@Components/IconButton';
import Introduction from '@Components/Introduction';
import DeleteModal from '@Components/Modal/DeleteModal';
import Remark from '@Components/Remark';
import theme from '@Components/Themes/Theme';
import { useDisclosure } from '@hooks/useDisclosure';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  IconButton,
  Typography,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useFieldArray, useWatch } from 'react-hook-form';
import { useGoalAndOperationalPlansForm } from '../../GoalAndOperationalPlansFormProvider';
import { IRiskManagementFormValues } from '../../GoalAndOperationalPlansFormProvider/types';
import ChanceOfRisk from './ChanceOfRisk';
import RiskCharacteristicsAndImpactsForm from './RiskCharacteristicsAndImpactsForm';
import RiskManagementForm from './RiskManagementForm';
import RiskNameForm from './RiskNameForm';
import DisclosureRiskManagement from '@Router/GoalAndOperationalPlans/Components/Disclosure/DisclosureRiskManagement';

const RiskManagement = ({ planIndex }: { planIndex: number }) => {
  const { fields, append, remove } = useFieldArray({
    name: `companyValueIncreasePlans.valueUpPlans.${planIndex}.riskManagement.risks`,
  });

  const [expandedPanels, setExpandedPanels] = useState<string[]>([]);
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const initialRender = useRef(true);

  const { control, reset, getValues } = useGoalAndOperationalPlansForm();

  const { introduction, remark } =
    useWatch({
      control,
      name: `companyValueIncreasePlans.valueUpPlans.${planIndex}.riskManagement`,
    }) ?? ({} as IRiskManagementFormValues);

  const completedSection = useWatch({
    control,
    name: 'completedSection',
  });

  const { isCompletedTh, isCompletedEn } = completedSection || {};

  const [indexSelected, setIndexSelected] = useState<number>(0);
  const [
    openConfirmDelete,
    { open: openConfirmDeleteDialog, close: closeConfirmDeleteDialog },
  ] = useDisclosure();

  const [
    openIntroduction,
    { open: openIntroductionDialog, close: closeIntroduction },
  ] = useDisclosure(!!introduction);

  const [openRemark, { open: openRemarkDialog, close: closeRemark }] =
    useDisclosure(!!remark);

  if (fields.length === 0) {
    append({});
  }

  const handleAddRisk = () => {
    const newIndex = fields.length;
    append({});
    setExpandedPanels((prev) => [...prev, `panel${newIndex}`]);
  };

  const handleOpenDeleteModal = (index: number) => {
    setIndexSelected(index);
    openConfirmDeleteDialog();
  };

  const handleSetExpanded =
    (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedPanels((prev) =>
        isExpanded ? [...prev, panel] : prev.filter((p) => p !== panel)
      );
    };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && initialRender.current) {
            setExpandedPanels((prev) => [...prev, 'panel0']);
            initialRender.current = false;
          }
        });
      },
      { threshold: 0.1 }
    );

    const currentSectionRef = sectionRef.current;
    if (currentSectionRef) {
      observer.observe(currentSectionRef);
    }

    return () => {
      if (currentSectionRef) {
        observer.unobserve(currentSectionRef);
      }
    };
  }, []);

  return (
    <>
      <Flex my={2} justifyContent={'space-between'} alignItems={'center'}>
        <Flex gap={1}>
          <Typography fontWeight={700}>การบริหารความเสี่ยง</Typography>
          <Flex gap={0.5} alignItems={'center'}>
            <IntroductionButton
              onClick={openIntroductionDialog}
              disabled={isCompletedTh && isCompletedEn}
            />
            <RemarkButton
              onClick={openRemarkDialog}
              disabled={isCompletedTh && isCompletedEn}
            />
          </Flex>
        </Flex>
        <DisclosureRiskManagement />
      </Flex>
      {openIntroduction && (
        <Box mb={2}>
          <Introduction
            onDelete={closeIntroduction}
            topic="การบริหารความเสี่ยง"
            setOpen={openIntroductionDialog}
            fieldPath={`companyValueIncreasePlans.valueUpPlans.${planIndex}.riskManagement.introduction`}
            isCompletedTh={isCompletedTh}
            isCompletedEn={isCompletedEn}
          />
        </Box>
      )}
      {openRemark && (
        <Box mb={2}>
          <Remark
            onDelete={closeRemark}
            topic="การบริหารความเสี่ยง"
            setOpen={openRemarkDialog}
            fieldPath={`companyValueIncreasePlans.valueUpPlans.${planIndex}.riskManagement.remark`}
            isCompletedTh={isCompletedTh}
            isCompletedEn={isCompletedEn}
          />
        </Box>
      )}
      <Box ref={sectionRef}>
        {fields.map((item, riskIndex) => (
          <Accordion
            key={item.id}
            expanded={expandedPanels.includes(`panel${riskIndex}`)}
            onChange={handleSetExpanded(`panel${riskIndex}`)}
            sx={{
              boxShadow: 'none',
              border: `1px solid ${theme.palette.grey[200]}`,
              borderRadius: '4px',
              marginBottom: 2,
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: 'black' }} />}
              aria-controls={`${item.id || riskIndex}-content`}
              id={`${item.id || riskIndex}-header`}
            >
              <Typography
                fontWeight={600}
                sx={{ flexGrow: 1 }}
                alignContent={'center'}
              >
                {`ประเด็นความเสี่ยงที่ ${riskIndex + 1} ที่เกี่ยวข้องกับแผน`}
              </Typography>
              {fields.length > 1 && (
                <IconButton
                  size="small"
                  edge="end"
                  aria-label="delete"
                  color="inherit"
                  onClick={() => handleOpenDeleteModal(riskIndex)}
                  disabled={isCompletedTh || isCompletedEn}
                >
                  <DeleteOutlined style={{ fontSize: '16px' }} />
                </IconButton>
              )}
            </AccordionSummary>
            <AccordionDetails>
              <RiskNameForm
                planIndex={planIndex}
                riskIndex={riskIndex}
                isCompletedTh={isCompletedTh}
                isCompletedEn={isCompletedEn}
              />
              <ChanceOfRisk
                planIndex={planIndex}
                riskIndex={riskIndex}
                isCompletedTh={isCompletedTh}
                isCompletedEn={isCompletedEn}
              />
              <RiskCharacteristicsAndImpactsForm
                planIndex={planIndex}
                riskIndex={riskIndex}
                isCompletedTh={isCompletedTh}
                isCompletedEn={isCompletedEn}
              />
              <RiskManagementForm
                planIndex={planIndex}
                riskIndex={riskIndex}
                isCompletedTh={isCompletedTh}
                isCompletedEn={isCompletedEn}
              />
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      <DeleteModal
        topic={`ประเด็นความเสี่ยงที่ ${indexSelected + 1} ที่เกี่ยวข้องกับแผน`}
        opened={openConfirmDelete}
        close={closeConfirmDeleteDialog}
        confirm={() => {
          remove(indexSelected);
          reset({
            companyValueIncreasePlans: {
              valueUpPlans: getValues().companyValueIncreasePlans.valueUpPlans,
            },
          });
          closeConfirmDeleteDialog();
        }}
      />

      <Box mt={2}>
        <AddButtonRounded
          name="เพิ่มประเด็นความเสี่ยงที่เกี่ยวข้องกับแผน"
          onClick={handleAddRisk}
          disabled={isCompletedTh || isCompletedEn}
        />
      </Box>
    </>
  );
};

export default RiskManagement;
